import { ReactNode, useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from "@azure/msal-browser";
import { msalConfig } from "./msalConfig";

export const msalInstance = new PublicClientApplication(msalConfig);

interface AuthProviderProps {
  children: ReactNode;
  instance?: PublicClientApplication;
}

export const AuthProvider = ({ children, instance }: AuthProviderProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const app = instance ?? msalInstance;

  useEffect(() => {
    const initializeMsal = async () => {
      await app.initialize();
      app.enableAccountStorageEvents();
      
      try {
        // Handle the redirect flow and get the response
        const response = await app.handleRedirectPromise();
        if (response) {
          // If we have a response, we came back from a redirect
          app.setActiveAccount(response.account);
        } else {
          // No redirect response, check if we have any accounts
          const accounts = app.getAllAccounts();
          if (accounts.length > 0) {
            app.setActiveAccount(accounts[0]);
          }
        }
      } catch (error) {
        console.error("Error during MSAL initialization:", error);
      }
      
      setIsInitialized(true);
    };

    initializeMsal();
  }, [app]);

  // Event handling for auth state changes
  useEffect(() => {
    const callbackId = app.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        app.setActiveAccount(payload.account);
      }
    });

    return () => {
      if (callbackId) {
        app.removeEventCallback(callbackId);
      }
    };
  }, [app]);

  if (!isInitialized) {
    return null;
  }

  return (
    <MsalProvider instance={app}>
      {children}
    </MsalProvider>
  );
};
